import {
  Button,
  List,
  ListItemText,
  Typography,
  styled as styled2,
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useEffect, useState } from "react";
import { T } from "react-translator-component";
import { useSelector } from "react-redux";
import { isRegularAppUser, isClosedGroup } from "../../util/utils";
import styled from "styled-components";
import { colors } from "../../common/colors";
import { styles } from "../../pages/styles";
import dayjs from "../../util/dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import appServices from "../../services/app-services/appServices";
import SubmitButton from "../SubmitButton";
import { ErrorMessageModal } from "../ErrorMessageModal";

dayjs.extend(isSameOrAfter);

let width = window.innerWidth * 0.9;
let height = window.innerHeight - 400;
// Styled components

const Container = styled.div`
  width: ${width};
  height: ${height};
  margin-top: 7px;
  padding: 12px;
  padding-top: 0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  border: 2 sol;
`;

const ParentDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ClearButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
  background-color: white;
`;

// Main component

interface Props {
  post: any;
  votedOptionId: number | null;
  closeModal(): void;
  onChange(votedOptionId: number | null): void;
}
const VotesModel = ({ closeModal, post, votedOptionId, onChange }: Props) => {
  const [viewStatus, setViewStatus] = useState<boolean>(false);
  const [voteOptions, setVoteOptions] = useState<any>(post?.vote_options);
  const [errorMessage, setErrorMessage] = useState<string[]>([])
  const [showErrorMessageModal, setShowErrorMessageModal] = useState<boolean>(false)
  const voteQuestion: string = post?.post_events.length > 0 ? post.post_events[0].voting_question : post.vote_question

  const { user } = useSelector((state: any) => state.userAuth);

  const calculatePercentage = (options: any[]) => {
    // Step 1: Calculate total votes
    let totalVotes = options.reduce(
      (total: any, option: { votes_count: any }) => total + option.votes_count,
      0
    );
    if (votedOptionId) totalVotes = totalVotes + 1

    // Step 2: Calculate and add percentage to each option
    let votesCountEachOption
    return options.map((option) => {
      votesCountEachOption = votedOptionId === option.id ? option.votes_count + 1 : option.votes_count
      const percentage =
        totalVotes > 0 ? (votesCountEachOption / totalVotes) * 100 : 0;
      return { ...option, percentage: percentage.toFixed(0) };
    });
  };

  useEffect(() => {
    const votes = calculatePercentage(post?.vote_options);
    setVoteOptions(votes);
  }, [post, votedOptionId]);

  const BorderLinearProgress = styled2(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor:
        theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: colors.farm_green500,
    },
  }));

  const handleVoteAnonymouslyNo = async (vote: any) => {
    try {
      await appServices.createVote(user.id, vote.id, false);
      onChange(vote.id)
      setViewStatus(true);
      return true
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? T("api_unknown_error"))
      setShowErrorMessageModal(true)
      return false
    }
  };

  const handleClickOpen = async (vote: any) => {
    if (!isRegularAppUser(user) ||
      post?.creator_id === user?.id ||
      post?.voted_info?.voted_by_me ||
      votedOptionId ||
      (post.post_events?.length > 0 && isClosedGroup(post.member_group.closed_at))
    ) {
      return false
    }

    const dateToCheck = dayjs(post?.expiry_date);
    const todayDate = dayjs().startOf("day");
    if (dateToCheck.isSameOrAfter(todayDate, "day")) {
      const sendApiResult = await handleVoteAnonymouslyNo(vote);
      return sendApiResult
    } else {
      return false
    }
  };

  return (
    <>
      <Container>
        <ParentDiv>
          <ClearButton
            onClick={() => {
              closeModal();
            }}
          >
            <Typography
              sx={{ fontSize: 12, color: colors.farm_gray200 }}
              variant="body2"
              color="textPrimary"
            >
              閉じる
            </Typography>
            <KeyboardArrowDownIcon />
          </ClearButton>
        </ParentDiv>
        <div style={{ height: 500, overflow: "auto" }}>
          <List
            sx={{
              "& .MuiListItemButton-root:hover": {
                bgcolor: colors.farm_gray10,
                "&, & .MuiListItemIcon-root": {
                  color: "black",
                },
                "&$focusVisible": {
                  backgroundColor: colors.farm_gray10,
                },
                "& .MuiListItemButton-root:touch": {
                  bgcolor: colors.farm_gray10,
                  "&, & .MuiListItemIcon-root": {
                    color: "black",
                  },
                },
              },
            }}
          >
            {voteQuestion && (
              <Typography sx={{ textAlign: "center", marginTop: 2 }}>
                {voteQuestion}
              </Typography>
            )}
            {voteOptions?.map((item: any) => (
              <SubmitButton
                key={item.id}
                sx={{
                  display: "flex",
                  backgroundColor: colors.farm_gray10,
                  borderRadius: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                  margin: "8px auto",
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor:
                    votedOptionId === item.id
                      ? colors.farm_green500
                      : colors.farm_green100,
                  fontFamily: "mplus-1c-bold",
                  color: colors.farm_black,
                  width: "100%",
                  textAlign: "left"
                }}
                validate={async () => true}
                onSubmit={() => handleClickOpen(item)}
              >
                <ListItemText
                  primary={
                    <span
                      style={{
                        ...styles.communiTitleDescrip,
                        wordWrap: "break-word",
                      }}
                    >
                      {item.option_text}
                    </span>
                  }
                  secondary={
                    viewStatus ? (
                      <div style={styles.detailVoteStatusTxt}>
                        <BorderLinearProgress
                          style={{ width: "90%" }}
                          variant="determinate"
                          value={Number(item.percentage)}
                        />
                        &nbsp;{item.percentage}&nbsp;%
                      </div>
                    ) : null
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />
              </SubmitButton>
            ))}
          </List>
          <div style={styles.detailStatusContainer}>
            {voteOptions?.length !== 0 ? (
              <Button
                disabled={!isRegularAppUser(user)}
                onClick={() => setViewStatus(!viewStatus)}
                style={{ borderRadius: 20 }}
                color={"secondary"}
                size="small"
                variant="outlined"
              >
                {viewStatus ? "投票に戻る" : T("view_voting_status")}
              </Button>
            ) : (
              <p style={styles.detailPostDate}>投票オプションはありません</p>
            )}
          </div>
        </div>
      </Container>
      <ErrorMessageModal
        messages={errorMessage}
        open={showErrorMessageModal}
        onClose={() => setShowErrorMessageModal(false)}
      />
    </>
  );
};

export default VotesModel;
